import type { ComponentProps, JSX, VoidComponent } from "solid-js";
import { createEffect, createMemo, createSignal, onMount } from "solid-js";
import { Dynamic } from "solid-js/web";
import { makeEventListener } from "@solid-primitives/event-listener";
import { isNil, isNumber } from "lodash-es";

type LineClamp = 2 | 3 | 4 | 5;
const lineClampClasses: Record<LineClamp, `line-clamp-${number}`> = {
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
  5: "line-clamp-5",
};

type TextProps = {
  id?: string;
  text: string | NonNullable<JSX.Element>;
  as?: "span" | "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  class?: string;
  classList?: Record<string, boolean>;
  truncate?: boolean | LineClamp;
  style?: ComponentProps<"p">["style"];
};
const Text: VoidComponent<TextProps> = (props) => {
  let ref!: HTMLSpanElement | HTMLHeadingElement;
  const shouldTruncate = createMemo(() => !isNil(props.truncate) && props.truncate !== false);
  const clampLines = createMemo(() => isNumber(props.truncate));
  const truncateClass = createMemo(() => (isNumber(props.truncate) ? lineClampClasses[props.truncate] : "truncate"));
  const [isTruncated, setIsTruncated] = createSignal(false);

  createEffect(() => {
    if (shouldTruncate()) {
      const addTooltipOnTruncate = () =>
        setIsTruncated(clampLines() ? ref.scrollHeight > ref.clientHeight : ref.scrollWidth > ref.clientWidth);

      onMount(() => {
        makeEventListener(ref, "mouseover", addTooltipOnTruncate);
      });
    }
  });

  return (
    <Dynamic
      id={props.id}
      component={props.as ?? "span"}
      ref={ref}
      class={props.class}
      classList={{
        [truncateClass()]: shouldTruncate(),
        "wrap-break-word": shouldTruncate(),
        ...props.classList,
      }}
      title={isTruncated() ? ref.innerText : undefined}
      {...(!isNil(props.style) && { style: props.style })}
    >
      {props.text}
    </Dynamic>
  );
};

export default Text;
